import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonExceptions = () => {
  return (
    <div className="tutorial-content-module">
      <div className="premium-header">
        <h1>Pro Exception Handling</h1>
        <p className="subtitle">Mastering the art of crashing gracefully. From Call Stacks to EAFP vs LBYL.</p>
      </div>

      <div className="content-section">
        <h2>1. The Big Idea (ELI5)</h2>
        <ProTip><p>
            Imagine a <strong>Factory Assembly Line</strong> (Your Code).
          </p>
          <ul>
            <li><strong>Standard Execution:</strong> Workers stick labels on boxes.</li>
            <li><strong>The Crash:</strong> A box arrives missing a lid. The worker panics and the entire factory shuts down (Crash!).</li>
            <li><strong>Exception Handling (`try-except`):</strong> You install a <strong>Safety Inspector</strong> at that station. When a lidless box arrives, instead of shutting down, the Inspector removes the box, logs a "Defect" note, and signals the worker to continue with the next box. The factory keeps running.</li>
          </ul></ProTip>
      </div>

      <div className="content-section">
        <h2>2. The Full Syntax: The 4 Blocks</h2>
        <p>
          Most beginners only know `try` and `except`. But professional Python code uses all four blocks to be semantic and clean.
        </p>
        <CodeBlock language="python" code={`def divide_secure(a, b):
    try:
        # 1. The Dangerous Code
        result = a / b
    except ZeroDivisionError as e:
        # 2. The Handler (Runs ONLY on error)
        print(f" Error caught: {e}")
        result = None
    else:
        # 3. The Success Block (Runs ONLY if NO error)
        # Why here? To keep the 'try' block small and focused!
        print(f"Calculation successful: {result}")
        save_to_database(result)
    finally:
        # 4. The Cleanup (Runs ALWAYS)
        print(" Cleaning up resources...")

divide_secure(10, 2)
# Output:
# Calculation successful: 5.0
#  Cleaning up resources...

divide_secure(10, 0)
# Output:
#  Error caught: division by zero
#  Cleaning up resources...`} />
      </div>

      <div className="content-section">
        <h2>3. Deep Dive: Call Stack Unwinding</h2>
        <p>
          Understanding <strong>how</strong> Python handles exceptions is key to debugging.
          When an error occurs (e.g., inside a nested function), Python creates an <strong>Exception Object</strong> and starts looking for a handler.
        </p>
        <p>
          It checks the current function. If no `try` block is found, it <strong>pops</strong> the stack frame and checks the *caller*. It repeats this ("Unwinding the Stack") until it hits the global scope. If still no handler? It invokes `sys.excepthook` (Printing the traceback) and kills the process.
        </p>
        <CodeBlock language="python" code={`def level_3():
    print("Level 3: About to crash...")
    x = 1 / 0 #  Error starts here

def level_2():
    level_3() # No handler here, unwinds to level_1

def level_1():
    try:
        level_2()
    except ZeroDivisionError:
        print("Caught the error in Level 1!")

level_1()
# Output:
# Level 3: About to crash...
# Caught the error in Level 1!`} />
      </div>

      <div className="content-section">
        <h2>4. Philosophy: EAFP vs LBYL</h2>
        <p>
          This is a famous Python philosophy debate.
        </p>
        <ul className="premium-list">
          <li><strong>LBYL (Look Before You Leap):</strong> Check everything <em>before</em> you do it. Common in C/Java.</li>
          <li><strong>EAFP (Easier to Ask Forgiveness than Permission):</strong> Just do it. If it fails, catch the error. Standard in Python.</li>
        </ul>
        <CodeBlock language="python" code={`# Style 1: LBYL (The Java Way)
import os
import { ProTip } from '../../components/LessonUI';
if os.path.exists("data.txt"):
    os.remove("data.txt")
else:
    print("File missing!")

# Style 2: EAFP (The Python Way)
try:
    os.remove("data.txt")
except FileNotFoundError:
    print("File missing!")`} />
        <h3>Why EAFP is better in Python?</h3>
        <p>
          <strong>Race Conditions.</strong> In the LBYL example, what if the file exists when you check `if`, but a hacker deletes it 1 millisecond later *before* your `remove` runs? Your code crashes.
          EAFP is <strong>atomic</strong>. You try to remove it. You either succeed or you don't. No timing gap.
        </p>
      </div>

      <div className="content-section">
        <h2>5. Performance: Is `try` slow?</h2>
        <p>
          Many developers fear exception handling is "expensive".
          <strong>The Truth:</strong> A `try` block with <em>setup cost</em> is effectively zero in modern Python 3.11+ (Zero-Cost Exceptions).
        </p>
        <ul className="premium-list">
          <li>If <strong>NO error</strong> occurs: EAFP is faster than LBYL (because you skip the `if` check).</li>
          <li>If <strong>an error</strong> occurs: EAFP is slower (creating the Exception object takes time).</li>
        </ul>
        <ProTip><p><strong>Rule of Thumb:</strong> Use EAFP for things that usually work (99% success). Use LBYL for things that often fail (e.g., user input validation).</p></ProTip>
      </div>

      <div className="content-section">
        <h2>6. Anti-Patterns to Avoid</h2>
        <p>
          Exception handling is powerful, but dangerous if misused.
        </p>
        <h3>1. The Bare Except (The Black Hole)</h3>
        <CodeBlock language="python" code={`#  TERRIBLE
try:
    process_data()
except: # Catches EVERYTHING, even SystemExit and KeyboardInterrupt!
    pass 

# Why bad? usage of Ctrl+C won't stop your program. 
# Typos (NameError) will be silenced.`} />

        <h3>2. Catching Exception (Too Broad)</h3>
        <CodeBlock language="python" code={`#  BAD
try:
    x = 1 / 0
except Exception: # Catches all logical errors
    print("Something went wrong")

# Why bad? You assume it's a math error, but it could be a MemoryError.
# You will spend hours debugging why your variables are None.`} />

        <h3>3. The Good Way</h3>
        <CodeBlock language="python" code={`# GOOD
try:
    process_data()
except (ValueError, TypeError) as e:
    logger.error(f"Data processing failed: {e}")`} />
      </div>

    </div>
  );
};

export default PythonExceptions;

